/*
 * File: Store.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2020 MacPractice, Inc. All rights reserved.
 *
 * Description: Store used for getting preferences from the API as well as applying any preference changes
 */
import DBPreferences from "services/preferences/DatabasePreferences";
import { types, flow, Instance } from "mobx-state-tree";
import { StoreManager } from "utils/useStore";
import { PreferenceBase } from "./models/PreferenceBase";
import { AllPrefs } from "./models/AllPrefs";

/**
 * The preferences store object. This model is responsible for loading the required preferences at log in, and applying updates to both the database and the cached preference objects.
 */
const Store = types
  .model("PreferencesStore", {
    /** The loaded preferences */
    preferences: types.optional(AllPrefs, {}),
    /** true when preferences have been loaded, false otherwise. This is used for asserting that preferences have been loaded before attempting to access them. */
    loadedPrefs: types.optional(types.boolean, false)
  })
  .actions(self => ({
    /**
     * Applies a new preference value for the given preference key.
     *
     * This method does not directly update the stored preference, instead it sends an update request to the server. The response contains the updated preference value, which is then applied to the stored preference just like it was being loaded for the first time.
     *
     * @param key The preference key to update. This is the "app" preference key, not the database pkey. Some preferences have different names in MPMobile that are unrelated to the database pkey, others don't. See the `preferences` function in this file for the mapping from app keys to database pkeys.
     * @param value The new preference value.
     */
    apply: flow(function*<K extends keyof Instance<typeof AllPrefs>>(
      // This parameter is using `keyof` to ensure that a known preference key is passed
      key: K,
      // This parameter is exploiting the fact that we have a type parameter representing a valid key, and indexing into that preference object, getting the parameters of it's update function, and using the type of the second parameter to ensure the expected type is passed.
      // If an error is occuring on this line after adding a new preference model type, ensure you've defined `valueForUpdate` the same way the other preference models do.
      value: Parameters<Instance<typeof AllPrefs>[K]["valueForUpdate"]>[1]
    ) {
      const prefSetting = self.preferences[key];
      if (prefSetting !== undefined) {
        // If the pref doesn't have an explicit pkey, it's key is the database pkey
        const serverKey = prefSetting.pkey ?? key;
        // Create the update object for the server.
        const serverValue = prefSetting.valueForUpdate(key, value);

        // Send the update, and receive the updated preference value.
        const newPrefs = yield DBPreferences.update(serverKey, serverValue);

        // Apply the updated server value to the preference object.
        yield prefSetting.applyLoadedPrefValue(newPrefs[serverKey]);
      } else {
        throw new Error(
          `Attempted to update unrecognized preference key "${key}"`
        );
      }
    }),
    getPreferences: flow(function*() {
      // This type cast is unchecked, but it works because `any` automatically coerces to the inferred type.
      // This type is only here to avoid accidentally using an invalid operation on the preferences in this method.
      const prefs: (Instance<typeof PreferenceBase> & {
        applyLoadedPrefValue: (any: any) => Promise<void>;
      })[] = Object.values(self.preferences);
      const pkeys = prefs.map(value => {
        return value.pkey;
      });

      const dbPrefs = yield DBPreferences.fetch(pkeys);

      let lastPref: Instance<typeof PreferenceBase> | undefined;
      try {
        for (const value of prefs) {
          lastPref = value;
          yield value.applyLoadedPrefValue(dbPrefs[value.pkey]);
        }
      } catch (error) {
        console.error(`Failed to apply loaded preference: ${lastPref?.pkey}`);
        throw error;
      }

      self.loadedPrefs = true;
      return self.preferences;
    })
  }))
  .views(self => ({
    get<K extends keyof Instance<typeof AllPrefs>>(
      keyName: K
    ): Instance<typeof AllPrefs>[K]["value"] {
      if (!self.loadedPrefs) {
        throw new Error(
          `Attempted to get preference key "${keyName}" before preferences loaded.`
        );
      }

      const value = self.preferences[keyName];

      if (value == null) {
        throw new Error(
          `Attempted to get unrecognized preference key "${keyName}"`
        );
      }

      return value.value;
    }
  }));

export default Store;

export const preferencePreload = async (storeManager: StoreManager) => {
  await storeManager.getStore(Store).getPreferences();
};
